import { parseDockerfile } from "./dockerfile";

export { parseDockerfile, tryParseDockerfile } from "./dockerfile";
export { extractVolumes } from "./volumes/directive";

// Web crypto (not node:crypto) so this also runs in Zero mutators in the
// browser. The layout is load-bearing: existing build_caches rows were hashed
// with a 4-byte big-endian prefix of the part's JS string length (UTF-16 code
// units, not byte length) followed by its UTF-8 bytes — changing either would
// invalidate every cached layer.
async function hashParts(parts: string[]): Promise<string> {
  const encoder = new TextEncoder();
  const chunks: Uint8Array[] = [];
  let total = 0;
  for (const part of parts) {
    // Length-prefix each part to prevent extension attacks
    const len = new Uint8Array(4);
    new DataView(len.buffer).setUint32(0, part.length, false);
    const bytes = encoder.encode(part);
    chunks.push(len, bytes);
    total += 4 + bytes.length;
  }
  const buf = new Uint8Array(total);
  let offset = 0;
  for (const chunk of chunks) {
    buf.set(chunk, offset);
    offset += chunk.length;
  }
  const digest = await crypto.subtle.digest("SHA-256", buf);
  return [...new Uint8Array(digest)]
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}

/** Sentinel stored for layers that completed but produced no cache tarball. */
export const COMPLETED_LAYER = "_";

/**
 * Sentinel for cache-output layers that executed and verifiably produced no
 * archive. Unlike COMPLETED_LAYER, which can reach a cache-output layer
 * without that layer ever running (a poisoned row pins the step broken, since
 * runs mount nothing for it), this value is only written after the layer
 * actually ran with these inputs, so it is safe to trust.
 */
export const EMPTY_LAYER = "-";
export const MAX_WORKER_CACHE_HASHES = 1024;

export type LayerCacheEntry = {
  cacheKey: string;
  layerHash: string;
  stale?: boolean;
  cacheOutputs?: string[];
};

export function layerHashes(cache: LayerCacheEntry[]): string[] {
  const hashes = new Set<string>();
  for (const entry of cache) {
    if (
      entry.layerHash &&
      entry.layerHash !== COMPLETED_LAYER &&
      entry.layerHash !== EMPTY_LAYER
    ) {
      hashes.add(entry.layerHash);
    }
  }
  return [...hashes];
}

export function countMissingLayerHashes(
  requiredHashes: readonly string[],
  availableHashes: ReadonlySet<string>,
  stopAt = Number.POSITIVE_INFINITY
): number {
  let missing = 0;
  for (const hash of requiredHashes) {
    if (availableHashes.has(hash)) continue;
    missing++;
    if (missing >= stopAt) break;
  }
  return missing;
}

export function boundedWorkerCacheHashes(hashes: Iterable<unknown>): string[] {
  const bounded: string[] = [];
  for (const hash of hashes) {
    if (typeof hash !== "string") continue;
    bounded.push(hash);
    if (bounded.length === MAX_WORKER_CACHE_HASHES) break;
  }
  return bounded;
}

// Namespace for the stock templates' pre-built layers in build_caches. Step
// rows are scoped (`spaceId:workflowId:stepDir:contentHash`), so a brand-new
// step matches nothing; a scoped miss falls back to its bare content hash
// under this prefix. The prefix keeps the platform-seeded rows apart from the
// unscoped per-step rows the API's createExecution writes.
export function templateCacheKey(contentHash: string): string {
  return `template:${contentHash}`;
}

export function contentHashOf(cacheKey: string): string {
  const i = cacheKey.lastIndexOf(":");
  return i === -1 ? cacheKey : cacheKey.slice(i + 1);
}

type StepFile = { path: string; content: string };

export const BUILD_CACHE_NAMESPACE = "build-cache-v3";

export async function computeLayerCache(
  files: StepFile[],
  previousCache: LayerCacheEntry[] = [],
  scope?: { spaceId: string; workflowId: string; stepDir: string }
): Promise<LayerCacheEntry[]> {
  const dockerfile = files.find((f) => f.path === "Dockerfile");
  if (!dockerfile) return [];

  const instructions = parseDockerfile(dockerfile.content);
  const filesByPath = new Map(files.map((f) => [f.path, f.content]));
  const layers: LayerCacheEntry[] = [];
  let prevKey = "";
  let pendingSources: string[] = [];

  for (const inst of instructions) {
    if (inst.kind === "copy") {
      pendingSources.push(...inst.sources);
      continue;
    }

    if (inst.kind !== "run") continue;

    const parts: string[] = [BUILD_CACHE_NAMESPACE, prevKey, inst.command];

    const sortedSources = [...pendingSources].sort();
    for (const rawSource of sortedSources) {
      const source = rawSource.startsWith("./")
        ? rawSource.slice(2)
        : rawSource;
      if (source === ".") {
        const sorted = files
          .filter((f) => f.path !== "Dockerfile")
          .sort((a, b) => a.path.localeCompare(b.path));
        for (const f of sorted) {
          parts.push(f.path, f.content);
        }
      } else if (filesByPath.has(source)) {
        parts.push(source, filesByPath.get(source)!);
      } else {
        const prefix = source.endsWith("/") ? source : source + "/";
        const matched = files
          .filter((f) => f.path.startsWith(prefix))
          .sort((a, b) => a.path.localeCompare(b.path));
        for (const f of matched) {
          parts.push(f.path, f.content);
        }
      }
    }
    pendingSources = [];

    const contentHash = await hashParts(parts);
    const cacheKey = scope
      ? `${scope.spaceId}:${scope.workflowId}:${scope.stepDir}:${contentHash}`
      : contentHash;
    const layerIdx = layers.length;
    const prev = previousCache[layerIdx];
    const cacheOutputs = inst.cacheOutputs;

    let layerHash = "";
    let stale = false;
    if (prev && prev.cacheKey === cacheKey) {
      layerHash = prev.layerHash;
    } else if (prev?.layerHash) {
      layerHash = prev.layerHash;
      stale = true;
    }

    prevKey = contentHash;
    const entry: LayerCacheEntry = { cacheKey, layerHash };
    if (stale) entry.stale = stale;
    if (cacheOutputs.length > 0) entry.cacheOutputs = cacheOutputs;
    layers.push(entry);
  }

  return layers;
}

export async function enrichCache(
  cache: LayerCacheEntry[],
  queryBuildCaches: (keys: string[]) => Promise<Map<string, string>>
): Promise<LayerCacheEntry[]> {
  if (cache.length === 0) return cache;
  const missing = cache.filter((e) => !e.layerHash).map((e) => e.cacheKey);
  const known =
    missing.length === 0 ? new Map() : await queryBuildCaches(missing);
  return cache.map((entry) => {
    const layerHash = entry.layerHash || known.get(entry.cacheKey) || "";
    return layerHash === entry.layerHash ? entry : { ...entry, layerHash };
  });
}

export function layerNeedsRebuild(entry: LayerCacheEntry): boolean {
  if (!entry.layerHash || entry.stale) return true;
  return (
    entry.layerHash === COMPLETED_LAYER && (entry.cacheOutputs?.length ?? 0) > 0
  );
}

export function needsBuild(cache: LayerCacheEntry[]): boolean {
  return cache.some(layerNeedsRebuild);
}
